import { Controller } from "@hotwired/stimulus"

// Repeatable form rows — the logistics form's appliances (docs/08).
//
// An exhibitor plugging in a fridge, a kettle and two monitors should not have
// to guess how many blank rows the page decided to give them. A row is added
// when they ask for one, and removed by marking it rather than by deleting it
// from the document: a saved row has to come back as `_destroy` so the server
// knows to remove it, and a row that is simply gone from the DOM says nothing
// at all.
//
// The template row carries `NEW_RECORD` where Rails put the index. Swapping it
// for a timestamp is the standard trick and it matters why: the index has to
// be unique among the rows ALREADY on the page, and a counter starting at zero
// collides with the first saved row the moment somebody removes one.
export default class extends Controller {
  static targets = ["rows", "template", "row", "addButton"]
  static values = { max: { type: Number, default: 20 } }

  add() {
    if (this.visibleRows.length >= this.maxValue) return

    const html = this.templateTarget.innerHTML.replace(/NEW_RECORD/g, new Date().getTime())
    this.rowsTarget.insertAdjacentHTML("beforeend", html)
    this.updateAdd()
    this.focusLastRow()
  }

  // A row the server has never seen can simply go. A saved one is hidden and
  // marked, because the server only learns to delete it from the `_destroy`
  // field coming back with the form.
  remove(event) {
    const row = event.currentTarget.closest("[data-repeatable-rows-target=row]")
    if (!row) return

    const destroy = row.querySelector("[name*='_destroy']")

    if (destroy) {
      destroy.value = "1"
      row.hidden = true
    } else {
      row.remove()
    }

    this.updateAdd()
  }

  get visibleRows() {
    return this.rowTargets.filter((row) => !row.hidden)
  }

  updateAdd() {
    if (this.hasAddButtonTarget) this.addButtonTarget.disabled = this.visibleRows.length >= this.maxValue
  }

  // Focus goes into the row that was just added. Somebody who pressed "Add"
  // and then has to hunt for where the cursor went has been given work, not
  // help.
  focusLastRow() {
    const last = this.visibleRows[this.visibleRows.length - 1]
    last?.querySelector("input, select, textarea")?.focus()
  }
}
